'use client';

import { AnimatedSection } from '@/components/marketing/AnimatedSection';
import { SEOHead } from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { useSanityPage } from '@/lib/sanity.hooks';
import {
  ArrowRight,
  Calculator,
  ChartLineUp,
  ChatText,
  Check,
  CloudArrowUp,
  CreditCard,
  Envelope,
  Files,
  Globe,
  MagnifyingGlass,
  Plugs,
  Ruler,
} from '@phosphor-icons/react';
import { motion, useReducedMotion } from 'framer-motion';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const categories = [
  { id: 'all', name: 'All Integrations' },
  { id: 'accounting', name: 'Accounting', icon: Calculator },
  { id: 'payments', name: 'Payments', icon: CreditCard },
  { id: 'marketing', name: 'Marketing', icon: ChartLineUp },
  { id: 'measurements', name: 'Measurements', icon: Ruler },
  { id: 'communication', name: 'Communication', icon: ChatText },
  { id: 'documents', name: 'Documents', icon: Files },
  { id: 'platform', name: 'Platform', icon: Globe },
];

const statusMap = {
  available: {
    text: 'Available',
    bg: 'bg-emerald-500/10',
    color: 'text-emerald-400',
    border: 'border-emerald-500/25',
  },
  beta: {
    text: 'Beta',
    bg: 'bg-amber-500/15',
    color: 'text-amber-400',
    border: 'border-amber-500/25',
  },
  'coming-soon': {
    text: 'Coming Soon',
    bg: 'bg-white/8',
    color: 'text-white/55',
    border: 'border-white/10',
  },
};

function LogoPlaceholder({ name, className = '' }: { name: string; className?: string }) {
  return (
    <div
      className={`w-full h-full rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-[0.65rem] font-bold text-white/70 uppercase tracking-wider ${className}`}
    >
      {name.slice(0, 2)}
    </div>
  );
}

const integrations = [
  {
    name: 'QuickBooks Online',
    description:
      'Two-way sync for customers, invoices, payments, and expenses. Eliminate manual data entry and reconcile in minutes.',
    category: 'accounting',
    status: 'available' as const,
    features: ['Invoice sync', 'Customer sync', 'Payment reconciliation', 'Expense import'],
    logo: '/images/integration-logos/quickbooks-online.jpg',
  },
  {
    name: 'Xero',
    description:
      'Connect your Xero accounting for streamlined bookkeeping and financial reporting.',
    category: 'accounting',
    status: 'beta' as const,
    features: ['Invoice sync', 'Bank reconciliation', 'Chart of accounts mapping'],
    logo: '/images/integration-logos/xero.jpg',
  },
  {
    name: 'Stripe',
    description:
      'Accept payments online, process deposits, and set up recurring billing. Automated payment reminders reduce accounts receivable.',
    category: 'payments',
    status: 'available' as const,
    features: ['Online payments', 'Deposit collection', 'Recurring billing', 'Automated reminders'],
    logo: null,
  },
  {
    name: 'Google Ads',
    description:
      'Track lead attribution from your ad campaigns. See which keywords and ads actually generate closed deals.',
    category: 'marketing',
    status: 'available' as const,
    features: ['ROI tracking', 'Conversion import', 'Campaign sync', 'Cost-per-lead analytics'],
    logo: '/images/integration-logos/google-ads.jpg',
  },
  {
    name: 'Google Analytics 4',
    description:
      'Track website visitors and conversions from the widget all the way into your CRM.',
    category: 'marketing',
    status: 'available' as const,
    features: ['Traffic sources', 'Event tracking', 'Conversion attribution', 'User behavior'],
    logo: '/images/integration-logos/google-analytics-4.jpg',
  },
  {
    name: 'Meta Ads',
    description: 'Connect Facebook and Instagram campaigns to lead records and closed revenue.',
    category: 'marketing',
    status: 'beta' as const,
    features: ['Lead form sync', 'Campaign attribution', 'Cost-per-job reporting'],
    logo: '/images/integration-logos/meta-ads.jpg',
  },
  {
    name: 'Google Business Profile',
    description: 'Manage reviews, posts, and business insights from the marketing dashboard.',
    category: 'marketing',
    status: 'available' as const,
    features: ['Review management', 'Post scheduling', 'Review request automation'],
    logo: '/images/integration-logos/google-business.jpg',
  },
  {
    name: 'EagleView',
    description:
      'Import industry-standard aerial measurement reports and auto-map facets to estimates.',
    category: 'measurements',
    status: 'available' as const,
    features: ['Report import', 'Auto-facet mapping', '3D models', 'Insurance-ready PDFs'],
    logo: '/images/integration-logos/eagleview.jpg',
  },
  {
    name: 'Hover 3D',
    description:
      '3D property measurements from smartphone photos for fast, insurance-grade reports.',
    category: 'measurements',
    status: 'beta' as const,
    features: ['Photo-to-3D', 'Material estimates', 'Design visualization'],
    logo: '/images/integration-logos/hover.jpg',
  },
  {
    name: 'Rooflyzer Sight',
    description:
      'Our proprietary AI satellite measurement tool. Address-only input, instant results, and pitch detection.',
    category: 'measurements',
    status: 'beta' as const,
    features: ['Address-only input', 'Instant measurements', 'Pitch detection', 'Bulk processing'],
    logo: null,
  },
  {
    name: 'Twilio',
    description: 'Programmable SMS, voice, and phone numbers for lead follow-up and call tracking.',
    category: 'communication',
    status: 'available' as const,
    features: ['SMS campaigns', 'Call routing', 'Number provisioning', 'Voicemail transcription'],
    logo: '/images/integration-logos/twilio.jpg',
  },
  {
    name: 'Gmail',
    description: 'Send emails directly from your Gmail account with templates and thread tracking.',
    category: 'communication',
    status: 'available' as const,
    features: ['Email sync', 'Template sending', 'Thread tracking', 'Signature support'],
    logo: '/images/integration-logos/gmail.jpg',
  },
  {
    name: 'Google Calendar',
    description: 'Sync appointments, job schedules, and team availability two ways.',
    category: 'communication',
    status: 'available' as const,
    features: ['Two-way sync', 'Availability checks', 'Team calendars', 'Appointment reminders'],
    logo: '/images/integration-logos/google-calendar.jpg',
  },
  {
    name: 'Cal.com',
    description: 'Let homeowners book inspections and consultations directly from your website.',
    category: 'communication',
    status: 'available' as const,
    features: ['Booking pages', 'Round-robin routing', 'Calendar sync', 'Custom intake forms'],
    logo: '/images/integration-logos/calcom.jpg',
  },
  {
    name: 'CompanyCam',
    description: 'Photo documentation for every job, synced to the project timeline.',
    category: 'documents',
    status: 'beta' as const,
    features: ['Project sync', 'Photo feed', 'Timeline view', 'Job-site checklists'],
    logo: '/images/integration-logos/companycam.jpg',
  },
  {
    name: 'Google Drive',
    description: 'Auto-backup contracts, invoices, and project files with folder automation.',
    category: 'documents',
    status: 'available' as const,
    features: ['File storage', 'Folder automation', 'Document backup', 'Shareable links'],
    logo: '/images/integration-logos/google-drive.jpg',
  },
  {
    name: 'Canva',
    description:
      'Create branded proposals, flyers, and marketing materials without leaving the workflow.',
    category: 'documents',
    status: 'beta' as const,
    features: ['Template library', 'Brand kit sync', 'Export to PDF', 'Direct import'],
    logo: '/images/integration-logos/canva.jpg',
  },
  {
    name: 'WordPress',
    description:
      'Embed the Instant Estimate Widget on any WordPress site with a simple shortcode or plugin.',
    category: 'platform',
    status: 'available' as const,
    features: ['Shortcode embed', 'Widget plugin', 'Lead capture', 'White-label styling'],
    logo: null,
  },
  {
    name: 'Beacon Pro+',
    description: 'Live pricing and direct material ordering from Beacon Roofing Supply.',
    category: 'platform',
    status: 'available' as const,
    features: ['Live pricing', 'Order placement', 'Availability checks', 'Delivery tracking'],
    logo: '/images/integration-logos/beacon-pro-plus.jpg',
  },
  {
    name: 'ABC Supply',
    description: 'Connect your ABC account for streamlined material ordering and invoice import.',
    category: 'platform',
    status: 'available' as const,
    features: ['Catalog sync', 'Order tracking', 'Invoice import', 'Branch lookup'],
    logo: '/images/integration-logos/abc-supply.jpg',
  },
  {
    name: 'QXO',
    description: 'Digital supply chain and logistics network for modern material procurement.',
    category: 'platform',
    status: 'coming-soon' as const,
    features: ['Digital supply chain', 'Real-time logistics', 'Order orchestration'],
    logo: '/images/integration-logos/qxo.jpg',
  },
  {
    name: 'SumoQuote',
    description: 'Import SumoQuote proposals and signed documents into the CRM and job pipeline.',
    category: 'platform',
    status: 'available' as const,
    features: ['Proposal import', 'Signed doc sync', 'Dashboard metrics', 'Status mapping'],
    logo: '/images/integration-logos/sumoquote.jpg',
  },
  {
    name: 'Webhook API',
    description:
      'Build custom integrations with your existing tools using our event-driven webhooks.',
    category: 'platform',
    status: 'available' as const,
    features: ['Event subscriptions', 'Zapier-compatible', 'Custom endpoints', 'Audit logs'],
    logo: null,
  },
];

function IntegrationCard({ integration }: { integration: (typeof integrations)[number] }) {
  const status = statusMap[integration.status];
  const category = categories.find((c) => c.id === integration.category);
  const [logoFailed, setLogoFailed] = useState(false);

  return (
    <div className="group h-full flex flex-col rounded-3xl bg-slate-900/60 border border-slate-800 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-slate-700">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700/80 flex items-center justify-center overflow-hidden">
            {integration.logo && !logoFailed ? (
              <img
                src={integration.logo}
                alt={`${integration.name} logo`}
                className="w-10 h-10 object-contain"
                loading="lazy"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <LogoPlaceholder name={integration.name} className="w-10 h-10 rounded-lg" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-white leading-tight">{integration.name}</h3>
            <p className="text-xs text-white/45 mt-0.5">{category?.name}</p>
          </div>
        </div>
        <span
          className={`px-2.5 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wider border ${status.bg} ${status.color} ${status.border}`}
        >
          {status.text}
        </span>
      </div>

      <p className="text-sm text-white/65 leading-relaxed mb-5 flex-grow">
        {integration.description}
      </p>

      <ul className="space-y-2.5 mb-6">
        {integration.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-white/70">
            <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" weight="bold" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        variant="outline"
        size="sm"
        className="w-full mt-auto rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700/60 hover:text-white"
        disabled={integration.status === 'coming-soon'}
        asChild={integration.status !== 'coming-soon'}
      >
        {integration.status === 'coming-soon' ? (
          <span>Notify Me</span>
        ) : (
          <Link to="/contact">
            Learn More
            <ArrowRight className="ml-2 size-4" />
          </Link>
        )}
      </Button>
    </div>
  );
}

export const IntegrationsPage = () => {
  const { data: cmsData } = useSanityPage('integrations');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const reduced = useReducedMotion();

  const filteredIntegrations = integrations.filter((i) => {
    const matchesCategory = activeCategory === 'all' || i.category === activeCategory;
    const matchesSearch =
      i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const pageTitle = cmsData?.seo?.metaTitle || 'Roofing Software Integrations — Connect Your Stack';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    cmsData?.description ||
    'Connect Rooflyzer with QuickBooks, Stripe, Google Ads, Twilio, WordPress, and more. Pre-built integrations for roofing contractors.';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/integrations"
        keywords="roofing software integrations, roofing CRM integrations, QuickBooks roofing, Stripe roofing, Google Ads roofing"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/social-card.svg'}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Integrations',
              item: 'https://rooflyzer.pro/integrations',
            },
          ],
        }}
      />

      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider mb-6">
              <Plugs className="w-4 h-4 text-emerald-400" weight="fill" />
              <span>Connect Your Stack</span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <h1 className="hero-h1 font-bold text-white mb-5">
              Works With What You <span className="text-white">Already Use.</span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="hero-lead text-white/65 max-w-2xl mx-auto mb-8">
              Pre-built integrations for accounting, payments, marketing, measurements,
              communication, and documents. No more copy-paste between tools.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="w-full sm:w-auto rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                asChild
              >
                <Link to="/demo">
                  Start Free Trial
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 px-8"
                asChild
              >
                <Link to="/contact">Request Integration</Link>
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 mb-10">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const isActive = activeCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 border ${
                        isActive
                          ? 'bg-white text-slate-950 font-semibold border-white'
                          : 'bg-slate-900/60 text-slate-300 border-slate-800 hover:bg-slate-800/60 hover:text-white'
                      }`}
                    >
                      {Icon && <Icon className="w-4 h-4" />}
                      <span>{cat.name}</span>
                    </button>
                  );
                })}
              </div>

              <div className="relative w-full md:w-72">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search integrations..."
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-slate-600 focus:ring-1 focus:ring-slate-600/50"
                />
              </div>
            </div>
          </AnimatedSection>

          <motion.div
            initial={reduced ? false : { opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredIntegrations.map((integration, i) => (
              <motion.div
                key={integration.name}
                initial={reduced ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: (i % 6) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              >
                <IntegrationCard integration={integration} />
              </motion.div>
            ))}
          </motion.div>

          {filteredIntegrations.length === 0 && (
            <div className="text-center py-20">
              <p className="text-white/55">
                No integrations found. Try a different search or category.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <div className="rounded-3xl bg-slate-900/60 border border-slate-800 p-8 md:p-12 flex flex-col lg:flex-row items-center gap-10 overflow-hidden">
              <div className="flex-1 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 font-mono text-xs uppercase tracking-wider">
                  <CloudArrowUp className="w-4 h-4 text-emerald-400" />
                  <span>Custom Integrations</span>
                </div>
                <h2 className="section-h2 font-bold text-white">
                  Need an integration we do not list?
                </h2>
                <p className="body-lg text-white/60 max-w-2xl">
                  We add new connectors every quarter based on demand. Submit a request and we will
                  scope the build, timeline, and whether it fits our public roadmap.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="lg"
                    className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                    asChild
                  >
                    <Link to="/contact">
                      <Envelope className="mr-2 size-5" />
                      Request Integration
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-lg border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 px-8"
                    asChild
                  >
                    <Link to="/demo">Talk to Sales</Link>
                  </Button>
                </div>
              </div>
              <div className="lg:w-2/5">
                <div className="rounded-2xl overflow-hidden border border-white/[0.08] bg-[#050507]">
                  <img
                    src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=1600&auto=format&fit=crop"
                    alt="Server racks representing secure API infrastructure"
                    className="w-full h-56 object-cover opacity-80"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};
